import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { format } from 'date-fns';

import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  debounce,
  FormControl,
  IconButton,
  InputBase,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useTheme
} from "@mui/material";
import type { GridColDef, GridPaginationModel, GridRowParams } from "@mui/x-data-grid";
import { AddCircleOutline, EditSquare, SearchOutlined } from "@mui/icons-material";

import { RootState } from "../../store/Index";
import { DataGrid } from "../../components/common/Index";
import { CreateRateDialog, EditRateDialog } from "./components";

import apiService from "../../service/Api";

import { TrashIcon } from "../../components/icons";
import { IFormProps, IRateCardProps } from "./types/rate-card";
import { tokens } from "../../theme";
import { User } from "../../store/slice/AuthSlice";
import { AxiosError } from "axios";
import ViewRateCardDialog from "./components/ViewRateCardDialog";

const RateCards: React.FC = () => {
  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10
  });
  const [rowCount, setRowCount] = useState(0);
  const [crewRates, setCrewRates] = useState<IRateCardProps[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string | undefined>();
  const [formData, setFormData] = useState<IFormProps>({
    role: {
      name: "",
      description: ""
    },
    roleId: null,
    hourly_rate: 0,
    stream_rate: 0,
    daily_rate: 0,
    salaried_rate: 0,
    fixed_rate: 0,
    currency: 'USD',
    effective_date: "",
    last_updated_by: ""
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<"all" | "hourly" | "stream" | "daily" | "salaried">("all");
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [openCreateRateDialog, setOpenCreateRateDialog] = useState<boolean>(false);
  const [rateType, setRateType] = useState<string>("");
  const [openViewRateCardDialog, setOpenViewRateCardDialog] = useState<boolean>(false);
  const [openEditRateDialog, setOpenEditRateDialog] = useState<boolean>(false);
  const [selectedRate, setSelectedRate] = useState<number | null>(null);
  
  const user = useSelector((state: RootState) => state.auth.user) as User | null;

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const primaryColor = reduxTheme.primary_colors?.[400] || "#724E41";
  const primaryTextColor = reduxTheme.primary_colors?.[400] || "#9c6644";
  const secondaryTextColor = reduxTheme.neutral_colors?.[100] || "#D6DDEB";
  const backgroundColor = colors.primary[800] || "#A9B2C3";

  const handleViewRateCard = () => {
    setOpenViewRateCardDialog(true);
  };

  const handleCloseViewRateCard = () => {
    setOpenViewRateCardDialog(false);
  };

  const fetchRates = useCallback(
    async () => {
      setLoading(true);
      setError(null);
      setWarning(null);

      try {
        const apiPage = pagination.page + 1;
        const params = {
          page: apiPage,
          page_size: pagination.pageSize,
          search: searchTerm || undefined
        };

        const response = await apiService.get<{
          count: number;
          results: IRateCardProps[];
        }>("/api/crews/role-ratings/", params);

        setCrewRates(response.data.results);
        setRowCount(response.data.count);
      } catch (error) {
        if (error instanceof AxiosError) {
          const errorMessage = error.response?.data?.message || "Failed to load rates. Please try again.";
          setError(errorMessage);
        } else {
          console.log({ error }, 'fetchRates error');
        }
      } finally {
        setLoading(false);
      }
    },
  [pagination, searchTerm]);

  useEffect(() => {
    fetchRates();
  }, [fetchRates])

  const columns: GridColDef<IRateCardProps>[] = [
    {
      field: "role",
      headerName: "Role",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ lineHeight: 1, py: 2 }}>
          <Typography variant="subtitle2">
            {params.row.role.name}
          </Typography>
        </Box>
      ),
    },
    {
      field: "rate",
      headerName: "Rates",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ lineHeight: 1, py: 2 }}>
          <Typography variant="subtitle2">
            {`From: ${params.row.currency} ${params.row.rate.hourly.amount} Per Hour`}
          </Typography>
        </Box>
      ),
    },
    {
      field: "effective_date",
      headerName: "Effective Date",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ py: 2, lineHeight: 1 }}>
          <Typography variant={'caption'}>
            {format(new Date(params.row.effective_date), 'MMM dd, yyyy')}
          </Typography>
        </Box>
      ),
    },
    {
      field: "last_updated_by",
      headerName: "Updated By",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", lineHeight: 1, my: 1, alignItems: "center", gap: 1 }}>
          <Avatar
            src={''}
            sx={{
              width: 32,
              height: 32,
              bgcolor: reduxTheme.primary_colors[800],
              color: reduxTheme.primary_colors[100],
            }}
          />
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {params.row.last_updated_by?.length ? params.row.last_updated_by : "N/A"}
            </Typography>
            <Typography variant="caption" fontWeight="medium">
              {`ID: ${params.row.last_updated_by?.length ? params.row.last_updated_by : "N/A"}`}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <Box display={'flex'} alignItems={'center'} sx={{ py: 1, lineHeight: 1 }} gap={1}>
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              setSelectedRate(params.row.id);
              setOpenEditRateDialog(true);
            }}>
            <EditSquare color="primary" sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton onClick={() => console.log('Deleting the rae card', params.row.role)}>
            <TrashIcon color="error" sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      ),
    },
  ];

  const resetForm = () => {
    setFormData({
      role: {
        name: "",
        description: ""
      },
      roleId: null,
      hourly_rate: 0,
      stream_rate: 0,
      daily_rate: 0,
      salaried_rate: 0,
      fixed_rate: 0,
      currency: 'USD',
      effective_date: "",
      last_updated_by: ""
    });
  };

  const onRateCreation = async () => {
    setFormLoading(true);
    try {
      const {
        effective_date,
        roleId,
        hourly_rate,
        stream_rate,
        daily_rate
      } = formData;

      if (!effective_date) {
        toast.error("Please provide the effective date for this rate card.");
        return;
      };

      if (!roleId) {
        toast.error("Please select the role for this rate card.");
        return;
      };

      if (!hourly_rate) {
        toast.error("Please provide the hourly rate amount for this rate card.");
        return;
      };

      if (!stream_rate) {
        toast.error("Please provide the per stream rate amount for this rate card.");
        return;
      };

      if (!daily_rate) {
        toast.error("Please provide the daily rate amount for this rate card.");
        return;
      };

      const ratePayload = {
        role: {
          name: formData.role.name,
          description: formData.role.description
        },
        role_id: roleId,
        rate: {
          hourly: {
            amount: hourly_rate,
            currency: 'USD',
            overtime_multiplier: 0,
            night_premium: 0,
            notes: '',
          },
          stream: {
            amount: stream_rate,
            currency: 'USD',
            overtime_multiplier: 0,
            night_premium: 0,
            notes: '',
          },
          daily: {
            amount: daily_rate,
            currency: 'USD',
            overtime_multiplier: 0,
            night_premium: 0,
            notes: '',
          },
          salaried: {
            amount: 0,
            currency: 'USD',
            overtime_multiplier: 0,
            night_premium: 0,
            notes: '',
          },
          fixed: {
            amount: 0,
            currency: 'USD',
            overtime_multiplier: 0,
            night_premium: 0,
            notes: '',
          }
        },
        currency: formData.currency,
        effective_date: effective_date
      };

      await apiService.post("/api/crews/role-ratings/", ratePayload);

      setCrewRates([]);
      toast.success("Rate card created successfully!", {
        position: "top-right",
        autoClose: 2000,
        theme: "light",
      });
      fetchRates();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.message || "Failed to create a rate card. Please try again.", {
          position: "top-right",
          autoClose: 2000,
          theme: "light",
        });
      } else {
        console.log('Failed to create a rate card', error);
      }
    } finally {
      setFormLoading(false);
      setOpenCreateRateDialog(false);
      setSelectedRoles(undefined);
      setRateType("");
      resetForm();
    }
  };

  const onRateEdit = async () => {
    console.log({ formData });
    setFormLoading(true);
    try {
      const {
        effective_date,
        roleId,
        hourly_rate,
        stream_rate,
        daily_rate
      } = formData;

      if (!effective_date) {
        toast.error("Please provide the effective date for this rate card.");
        return;
      };

      if (!roleId) {
        toast.error("Please select the role for this rate card.");
        return;
      };

      if (!hourly_rate) {
        toast.error("Please provide the hourly rate amount for this rate card.");
        return;
      };

      if (!stream_rate) {
        toast.error("Please provide the per stream rate amount for this rate card.");
        return;
      };

      if (!daily_rate) {
        toast.error("Please provide the daily rate amount for this rate card.");
        return;
      };

      const ratePayload = {
        role: {
          name: formData.role.name,
          description: formData.role.description
        },
        role_id: roleId,
        rate: {
          hourly: {
            amount: hourly_rate,
            currency: 'USD',
            overtime_multiplier: 0,
            night_premium: 0,
            notes: '',
          },
          stream: {
            amount: stream_rate,
            currency: 'USD',
            overtime_multiplier: 0,
            night_premium: 0,
            notes: '',
          },
          daily: {
            amount: daily_rate,
            currency: 'USD',
            overtime_multiplier: 0,
            night_premium: 0,
            notes: '',
          },
          salaried: {
            amount: 0,
            currency: 'USD',
            overtime_multiplier: 0,
            night_premium: 0,
            notes: '',
          },
          fixed: {
            amount: 0,
            currency: 'USD',
            overtime_multiplier: 0,
            night_premium: 0,
            notes: '',
          }
        },
        currency: formData.currency,
        effective_date: effective_date,
        created_by: user?.id,
        last_updated_by: user?.id
      };

      await apiService.patch(`/api/crews/role-ratings/${selectedRate}/`, ratePayload);

      setCrewRates([]);
      toast.success("Rate card updated successfully!", {
        position: "top-right",
        autoClose: 2000,
        theme: "light",
      });
      fetchRates();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.message || "Failed to update rate card. Please try again.", {
          position: "top-right",
          autoClose: 2000,
          theme: "light",
        });
      } else {
        console.log('Failed to update rate card', error);
      }
    } finally {
      setFormLoading(false);
      setOpenEditRateDialog(false);
      setSelectedRoles(undefined);
      setRateType("");
      resetForm();
    }
  };

  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setSearchTerm(value), 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.clear();
    };
  }, [debouncedSetSearchTerm]);

  const handleRolesChange = (role: string | null) => {
    if (role) {
      const regexed = role.match(/^(.*?)\s-\s(.*)$/);

      setSelectedRoles(role);
      if (regexed) {
        setFormData((prev: IFormProps) => (
          {
            ...prev,
            role: {
              name: regexed[2],
              description: regexed[2]
            },
            roleId: parseInt(regexed[1])
          }
        ));
      }
    }
  };

  const handleRolesEdit = (role: string | null) => {
    if (role) {
      const regexed = role.match(/^(.*?)\s-\s(.*)$/);

      setSelectedRoles(role);
      if (regexed) {
        setFormData((prev: IFormProps) => (
          {
            ...prev,
            role: {
              name: regexed[2],
              description: regexed[2]
            },
            roleId: parseInt(regexed[1])
          }
        ));
      }
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  // Handling pagination when user changes the pagination model
  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPagination(newModel);
  };

  const handleRowClick = (params: GridRowParams<IRateCardProps>) => {
    setSelectedRate(params.row.id);
    handleViewRateCard();
  };

  if (!crewRates) return null;

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Box sx={{ height: "100%", background: "#FFF", padding: "24px", borderRadius: 5, boxShadow: 0 }}>
        <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} mb={1} gap={1}>
          <Box display={'flex'} alignItems={'center'} gap={1}>
            <Box
              sx={{
                backgroundColor: backgroundColor,
                border: '1px solid',
                borderRadius: 2,
                borderColor: 'primary',
                display: "flex",
                alignItems: "center",
                pl: 1,
                flex: 1,
                minWidth: 320,
              }}
            >
              <SearchOutlined sx={{ fontSize: 24, mr: 1, color: secondaryTextColor }} />
              <InputBase
                placeholder="Search by role..."
                sx={{ padding: .4, flex: 1, color: primaryTextColor }}
                onChange={(e) => debouncedSetSearchTerm(e.target.value)}
              />
            </Box>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel
                id="rate-type-label"
                sx={{ color: primaryTextColor }}
              >
                Rate Type
              </InputLabel>
              <Select
                labelId="rate-type-label"
                id="rate-type-label"
                value={filterType}
                label="Rate Type"
                onChange={(e) => setFilterType(e.target.value)}
                sx={{
                  background: backgroundColor,
                  color: primaryTextColor,
                  fontSize: 12,
                  borderRadius: 2,
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: primaryColor,
                  },
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="hourly">Hourly</MenuItem>
                <MenuItem value="stream">Per Stream</MenuItem>
                <MenuItem value="daily">Per Day</MenuItem>
                <MenuItem value="fixed">Fixed</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Button
            size={"small"}
            startIcon={<AddCircleOutline />}
            onClick={() => setOpenCreateRateDialog(true)}
            sx={{ textTransform: 'capitalize' }}
            variant="contained">
              Add Rate Card
          </Button>
        </Box>

        {loading ? (
          <CircularProgress color={'primary'} size={16} />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : warning ? (
          <Alert severity="warning">{warning}</Alert>
        ) : (
          <Box>
            {crewRates.length > 0 && (
              <DataGrid
                rows={crewRates}
                columns={columns}
                rowCount={rowCount}
                loading={loading}
                paginationMode="server"
                paginationModel={pagination}
                onRowClick={handleRowClick}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={[5, 10, 15, 20]}
                sx={{
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor:
                      reduxTheme.primary_colors?.[500] || "#614438",
                    color: reduxTheme.primary_colors?.[100] || "#fff",
                  },
                  "& .MuiDataGrid-columnHeader": {
                    backgroundColor:
                      reduxTheme.primary_colors?.[500] || "#614438",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    color: reduxTheme.primary_colors?.[100] || "#fff",
                  },
                  "& .MuiDataGrid-footerContainer": {
                    backgroundColor: "#fff",
                    justifyContent: "center",
                    padding: 1,
                  },
                }}
              />
            )}
          </Box>
        )}
      </Box>
      {/* All Dialogs */}
      <CreateRateDialog
        formData={formData}
        formLoading={formLoading}
        handleCreateRate={onRateCreation}
        handleInputChange={handleInputChange}
        handleRolesChange={handleRolesChange}
        setDialogOpen={setOpenCreateRateDialog}
        open={openCreateRateDialog}
        rateType={rateType}
        resetForm={resetForm}
        selectedRoles={selectedRoles}
        setSelectedRoles={setSelectedRoles}
        setRateType={setRateType}
      />

      {selectedRate && (
        <EditRateDialog
          formData={formData}
          formLoading={formLoading}
          handleEditRate={onRateEdit}
          handleInputChange={handleInputChange}
          handleRolesEdit={handleRolesEdit}
          setDialogOpen={setOpenEditRateDialog}
          open={openEditRateDialog}
          rateType={rateType}
          resetForm={resetForm}
          selectedRate={selectedRate}
          selectedRoles={selectedRoles}
          setFormData={setFormData}
          setFormLoading={setFormLoading}
          setSelectedRoles={setSelectedRoles}
          setRateType={setRateType}
        />
      )}

      <ViewRateCardDialog
        open={openViewRateCardDialog}
        onClose={handleCloseViewRateCard}
        selectedRate={selectedRate}
      />
    </Box>
  );
};

export default RateCards;
